import { useEffect, useState } from "react";

import { Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useAiModels, useGenerateAiNote, type AiProvider } from "../api";
import { weekLabel } from "../lib";

const PROVIDER_LABEL: Record<AiProvider, string> = {
  ollama: "Local",
  anthropic: "Claude",
  openai: "OpenAI",
};

/**
 * "Generate with AI" button + modal: pick one of the app-configured LLMs
 * (local Ollama models, Claude, OpenAI — whatever Settings → AI has keys for)
 * and (re)generate the AI note for the run or for one week. The model reads
 * the full run context including the team's human comments.
 */
export function GenerateAiDialog({
  runId,
  weekEnding,
}: {
  runId: string;
  /** null → the run-level assumptions note; a date → that week's analysis. */
  weekEnding: string | null;
}) {
  const [open, setOpen] = useState(false);
  const models = useAiModels(open);
  const [modelId, setModelId] = useState<string | null>(null);
  const mutation = useGenerateAiNote();

  useEffect(() => {
    if (modelId === null && models.data?.default_id) setModelId(models.data.default_id);
  }, [models.data?.default_id, modelId]);

  const generate = () =>
    mutation.mutate(
      { run_id: runId, week_ending: weekEnding, model_id: modelId },
      { onSuccess: () => setOpen(false) },
    );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) mutation.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          Generate with AI
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {weekEnding
              ? `Generate AI analysis — WE ${weekLabel(weekEnding, true)}`
              : "Generate AI assumptions & comments"}
          </DialogTitle>
          <DialogDescription>
            The model reads the full forecast run — weekly flows, monthly collections, the
            cash budget — plus the team's comments, and writes{" "}
            {weekEnding ? "this week's variance analysis" : "the run-level assumptions"}.
            Regenerating replaces the previous AI note.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">Model</span>
          <Select value={modelId ?? ""} onValueChange={(v) => setModelId(v)}>
            <SelectTrigger>
              <SelectValue
                placeholder={models.isLoading ? "Loading models…" : "Pick a model"}
              />
            </SelectTrigger>
            <SelectContent>
              {(models.data?.models ?? []).map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {PROVIDER_LABEL[m.provider]} · {m.name}
                  {m.is_default ? " (default)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {models.data && models.data.models.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No models available — the local LLM is unreachable and no Claude/OpenAI key is
              configured under Settings → AI.
            </p>
          )}
          {mutation.isError && (
            <p className="text-sm text-danger">Generation failed: {mutation.error.message}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={generate} disabled={!modelId || mutation.isPending}>
            {mutation.isPending ? "Generating…" : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
